import { Stack } from '@ui-components/neo.tax';
import { useEffect } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import {
  AnalysisType,
  OnboardingFlowStage,
  useBackgroundOperations,
  useOnboardingFlowStage,
  useUnfinishedSyncEventInPeriodByAnalysisType,
  AnalysisInfo,
  GetStartedHero,
  OnboardingFlowProcessingHero,
} from '@neo.tax/shared';
import { BackgroundOperationName } from '@neo.tax/shared/graphql';

import { RdProjectsLayout } from '../rd-projects-layout';

export const RdProjectsLoader = (): React.ReactElement => {
  const { stage, startPolling, stopPolling } = useOnboardingFlowStage();
  const { fiscalYearId, period, periodName } = useRdContext();
  const { period: unfinishedSyncEventsPeriod } = useUnfinishedSyncEventInPeriodByAnalysisType({
    fiscalYearId,
    periodName,
    analysisType: AnalysisType.Rd,
  });
  const { backgroundOperations } = useBackgroundOperations();

  // While a CSV import is creating projects, render the (lockable) projects layout rather than the
  // onboarding heroes — the page should be blocked with a processing message, like the RD cost import.
  const isRdProjectsImportInProgress =
    backgroundOperations?.some(
      (op) => op.name === BackgroundOperationName.ImportRdProjectsFileOperation && op.progressPercent < 100,
    ) ?? false;
  useEffect(() => {
    // Start polling after the user submits their data and the stage changes to DATA_PROCESSING_IN_PROGRESS
    if (stage === OnboardingFlowStage.DATA_PROCESSING_IN_PROGRESS) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [stage, startPolling, stopPolling]);

  if (
    isRdProjectsImportInProgress ||
    stage === OnboardingFlowStage.DATA_PROCESSING_COMPLETED ||
    (stage === OnboardingFlowStage.DATA_PROCESSING_IN_PROGRESS && !unfinishedSyncEventsPeriod)
  ) {
    return <RdProjectsLayout />;
  }

  return (
    <Stack fullHeight direction="column" paddingY={8} paddingX={6} gap={6}>
      {stage === OnboardingFlowStage.INITIAL ? (
        <GetStartedHero
          analysisType={AnalysisType.Rd}
          fiscalYearId={fiscalYearId}
          periodName={periodName}
          period={period}
        />
      ) : (
        <OnboardingFlowProcessingHero />
      )}
      <AnalysisInfo />
    </Stack>
  );
};
